import {
  ComponentEntity,
  GroupEntity,
  UserEntity,
} from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import Alert from '@material-ui/lab/Alert';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import { buildOrganizationInheritanceDiagram } from './buildOrganizationInheritanceDiagram';
import { OrganizationInheritanceDiagram } from './OrganizationInheritanceDiagram';

type OrganizationInheritanceDiagramDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function OrganizationInheritanceDiagramDialog({
  open,
  onClose,
}: OrganizationInheritanceDiagramDialogProps) {
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();
  const [dialogFullscreen, setDialogFullscreen] = useState(false);

  const handleCatalogNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  useEffect(() => {
    if (!open) {
      setDialogFullscreen(false);
    }
  }, [open]);

  const diagramState = useAsync(async () => {
    if (!open) {
      return undefined;
    }

    const response = await catalogApi.getEntities({
      filter: { kind: ['Group', 'Component', 'User'] },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.annotations',
        'metadata.title',
        'spec',
      ],
    });

    const groups = response.items.filter(
      entity => entity.kind === 'Group',
    ) as GroupEntity[];
    const components = response.items.filter(
      entity => entity.kind === 'Component',
    ) as ComponentEntity[];
    const users = response.items.filter(
      entity => entity.kind === 'User',
    ) as UserEntity[];

    return buildOrganizationInheritanceDiagram(groups, components, users);
  }, [catalogApi, open]);

  const model = useMemo(() => diagramState.value, [diagramState.value]);
  const canvasHeight = dialogFullscreen ? '42vh' : 520;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={dialogFullscreen}
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Organization graph</span>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <IconButton
              aria-label={dialogFullscreen ? 'Exit expanded view' : 'Expand diagram'}
              onClick={() => setDialogFullscreen(current => !current)}
              size="small"
            >
              {dialogFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {diagramState.loading ? <Progress /> : null}
        {diagramState.error ? <Alert severity="error">{diagramState.error.message}</Alert> : null}
        {!diagramState.loading && !diagramState.error && model ? (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Interactive org tree starting collapsed at Invisible Technologies. Use Members view
              to see who belongs to each team, or Repos view for Jira board inheritance.
            </Typography>
            <OrganizationInheritanceDiagram
              officialRoot={model.officialRoot}
              unofficialRoot={model.unofficialRoot}
              onCatalogNavigate={handleCatalogNavigate}
              canvasHeight={canvasHeight}
            />
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
